// Settings-catalog API (ml-scribe `domains/settings`). First FE consumer of
// the generic settings surface — kept purpose-specific to the shared patient
// directory rather than minting a generic settings client for one key.
// Base path: /api/v2/ml-scribe/settings.
import { getScribeHttpClients } from '@heidi/api/client';

/**
 * The workspace-home consent key. A FROZEN EXTERNAL IDENTITY — the ml-scribe
 * settings catalog targets the setting by it (`SHARE_PATIENTS_ENABLED_KEY`
 * in `domains/settings/catalog/keys.py`).
 */
export const SHARE_PATIENTS_ENABLED_KEY = 'workspace-share_patients_enabled';

// ONE literal on purpose: the migration gate derives route-prefix ownership
// from a domain's route literals, and two `/settings/**` claims would hand the
// whole `/settings/` namespace to workspace — colliding with the app's
// `/settings/**` PAGE routes (middleware matcher included). A single claim
// stays under the gate's two-claim floor, which is the intended reading for a
// one-key API client like this.
const SETTINGS_ROUTE = '/settings';

/** One resolved setting: effective value, type schema, and provenance. */
export type ResolvedSettingResponse = {
  value: unknown;
  value_type: Record<string, unknown>;
  source: string;
  locked: boolean;
  own_value: unknown;
};

/** @domain workspace */
export async function getSharedPatientDirectorySetting(): Promise<ResolvedSettingResponse> {
  const { data } = await getScribeHttpClients().scribeHttpV2.get<ResolvedSettingResponse>(
    `${SETTINGS_ROUTE}/${SHARE_PATIENTS_ENABLED_KEY}`
  );
  return data;
}

/**
 * Enable the shared patient directory for the active workspace.
 *
 * A write-once consent: the backend accepts ONLY a literal JSON `true`
 * (coercible stand-ins are 422-rejected) and rejects every later write with
 * 409 — enabled is irreversible through any API. A 409 also encodes
 * "unavailable" (the release flag is off for the workspace), so callers
 * refetch the resolved value rather than interpreting the status alone.
 *
 * @domain workspace
 */
export async function enableSharedPatientDirectory(): Promise<void> {
  await getScribeHttpClients().scribeHttpV2.patch(SETTINGS_ROUTE, {
    [SHARE_PATIENTS_ENABLED_KEY]: true,
  });
}
